import React, { useState, useEffect, useCallback, useRef } from 'react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? '';

// Strip the leading H1/H2 line from a section's markdown so the renderer
// doesn't show the heading twice (once in our wrapper, once in the content).
function stripLeadingHeading(md) {
  if (!md) return '';
  return md.replace(/^\s*#{1,3}\s+[^\n]+\n+/, '');
}

// Minimal markdown → HTML for proposal content. Not a full parser — we only
// need headings, bullets, bold, italic, and paragraphs. Tradeoff: avoids a
// new dependency for a one-feature UI surface.
function renderMarkdown(md) {
  if (!md) return null;
  const html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.*)$/gm, '<h3 style="font-size:13px;font-weight:700;margin:14px 0 6px;color:var(--text-0)">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 style="font-size:15px;font-weight:700;margin:18px 0 8px;color:var(--text-0);border-bottom:1px solid var(--border);padding-bottom:4px">$1</h2>')
    .replace(/^# (.*)$/gm, '<h1 style="font-size:17px;font-weight:700;margin:20px 0 10px;color:var(--text-0)">$1</h1>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/^- (.*)$/gm, '<li style="margin:3px 0 3px 18px">$1</li>')
    .replace(/^(\d+)\. (.*)$/gm, '<li style="margin:3px 0 3px 18px">$2</li>')
    .replace(/\n\n+/g, '</p><p style="margin:8px 0;line-height:1.6">')
    .replace(/^([^<\n].*)$/gm, '$1');
  return <div style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-1)' }} dangerouslySetInnerHTML={{ __html: '<p style="margin:8px 0;line-height:1.6">' + html + '</p>' }} />;
}

function Section({ title, content, busy, error, badge }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div style={{ marginBottom: 18, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: content || busy ? '1px solid var(--border)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-0)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
          {badge && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', padding: '1px 6px', borderRadius: 3, background: 'var(--bg-3)', color: 'var(--text-2)' }}>{badge}</span>}
          {busy && <span style={{ fontSize: 10, color: 'var(--text-2)' }}>working…</span>}
        </div>
        {content && (
          <button onClick={onCopy} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', padding: '3px 8px', borderRadius: 3, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer' }}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        )}
      </div>
      {error && (
        <div style={{ padding: '10px 14px', color: 'var(--red)', fontSize: 12 }}>Error: {error}</div>
      )}
      {content && (
        <div style={{ padding: '8px 16px 14px' }}>{renderMarkdown(stripLeadingHeading(content))}</div>
      )}
    </div>
  );
}

export default function BdTeamPanel({ opp, onClose }) {
  const [draft, setDraft] = useState(opp?.bdTeamDraft || null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const fetchDraft = useCallback(async () => {
    if (!opp?.noticeId) return null;
    const token = localStorage.getItem('bd_auth_token');
    const r = await fetch(`${BASE_URL}/api/opportunities/${opp.noticeId}/bd-team`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) return null;
    const data = await r.json();
    return data.bdTeamDraft;
  }, [opp?.noticeId]);

  // Poll every 4s while the run is in flight. Stop as soon as status flips.
  useEffect(() => {
    if (!polling) return;
    pollRef.current = setInterval(async () => {
      const d = await fetchDraft();
      if (d) setDraft(d);
      if (d && (d.status === 'complete' || d.status === 'partial' || d.status === 'failed')) {
        setPolling(false);
        clearInterval(pollRef.current);
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [polling, fetchDraft]);

  const start = async () => {
    const token = localStorage.getItem('bd_auth_token');
    const r = await fetch(`${BASE_URL}/api/opportunities/${opp.noticeId}/bd-team/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      alert('Could not start: ' + (err.error || r.status));
      return;
    }
    setDraft({ status: 'running', startedAt: new Date().toISOString() });
    setPolling(true);
  };

  const startProposal = async () => {
    const ok = window.confirm(
      'Generate the proposal draft?\n\n' +
      'This is Phase 2 — only run this after reading the Executive Decision and deciding to pursue. ' +
      'Costs an additional ~$0.20-0.30 of Anthropic credits.\n\n' +
      'Continue?'
    );
    if (!ok) return;
    const token = localStorage.getItem('bd_auth_token');
    const r = await fetch(`${BASE_URL}/api/opportunities/${opp.noticeId}/bd-team/proposal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      alert('Could not start proposal: ' + (err.error || r.status));
      return;
    }
    setDraft(prev => ({ ...prev, status: 'running', phase: 'proposal' }));
    setPolling(true);
  };

  const reset = async () => {
    if (!window.confirm('Clear this draft? You can re-run the BD team afterward.')) return;
    const token = localStorage.getItem('bd_auth_token');
    await fetch(`${BASE_URL}/api/opportunities/${opp.noticeId}/bd-team`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    setDraft(null);
    setPolling(false);
  };

  const copyAll = async () => {
    const parts = [
      '# Opportunity Identity Resolution\n\n' + (draft?.identity || ''),
      '\n\n# Research Brief\n\n' + (draft?.research || ''),
      '\n\n# Business Value Scorecard\n\n' + (draft?.businessValue || ''),
      '\n\n# Red Team Review (Capture)\n\n' + (draft?.redTeam || ''),
      '\n\n# Executive Decision — The $40K Test\n\n' + (draft?.executiveDecision || ''),
    ];
    // Phase 2 sections only get included if the user requested a proposal draft.
    if (draft?.draft || draft?.finalDraft) {
      parts.push('\n\n# Draft Proposal\n\n' + (draft?.draft || ''));
      parts.push('\n\n# Red Team Review (Proposal)\n\n' + (draft?.proposalRedTeam || ''));
      parts.push('\n\n# Final Draft (Red-Team-Informed Revision)\n\n' + (draft?.finalDraft || ''));
    }
    try {
      await navigator.clipboard.writeText(parts.join(''));
      alert('All sections copied. Paste into Google Docs or Word.');
    } catch {
      alert('Could not access clipboard.');
    }
  };

  const status = draft?.status;
  const running = status === 'running';
  const hasAnything = !!(draft?.research || draft?.draft || draft?.redTeam);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, width: 'min(720px, 100vw)', height: '100vh',
      background: 'var(--bg-0)', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 32px rgba(0,0,0,0.3)',
      zIndex: 1000, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>BD Team</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opp?.title || 'Opportunity'}</div>
        </div>
        <button onClick={onClose} style={{ fontSize: 18, padding: '2px 10px', background: 'transparent', border: 'none', color: 'var(--text-1)', cursor: 'pointer' }}>×</button>
      </div>

      <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {!draft && (
          <button onClick={start} style={{ fontSize: 12, padding: '6px 14px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
            ▶ Run capture review
          </button>
        )}
        {running && <span style={{ fontSize: 12, color: 'var(--text-1)' }}>Working… typical run is 60-90 seconds.</span>}
        {hasAnything && !running && (
          <>
            <button onClick={copyAll} style={{ fontSize: 12, padding: '6px 14px', background: 'var(--bg-3)', color: 'var(--text-0)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>
              Copy all sections
            </button>
            <button onClick={start} style={{ fontSize: 12, padding: '6px 14px', background: 'var(--bg-3)', color: 'var(--text-0)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>
              Re-run capture review
            </button>
            <button onClick={reset} style={{ fontSize: 12, padding: '6px 10px', background: 'var(--bg-3)', color: 'var(--red)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>
              Clear
            </button>
          </>
        )}
        {status === 'failed' && <span style={{ fontSize: 12, color: 'var(--red)' }}>Run failed: {draft?.error || 'unknown error'}</span>}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
        {!draft && (
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.65 }}>
            <p style={{ marginBottom: 10, color: 'var(--text-0)', fontWeight: 600 }}>Two-phase BD Team workflow:</p>
            <p style={{ marginBottom: 8 }}><strong>Phase 1 — Capture Review</strong> (runs first, ~$0.30):</p>
            <ol style={{ paddingLeft: 22, lineHeight: 1.7, marginBottom: 14 }}>
              <li><strong>Identity Resolution</strong> — verifiable identifiers extracted from the solicitation</li>
              <li><strong>Capture Playbook</strong> — acquisition intelligence, evidence-backed competitors, relationships, readiness, and win posture</li>
              <li><strong>Business Value Scorecard</strong> — 9-dimension executive framing (revenue, strategic value, PWin, etc.)</li>
              <li><strong>Red Team of the research</strong> — adversarial checklist + central thesis falsification</li>
              <li><strong>Executive Decision</strong> — the "$40K test": YES / NO / CONDITIONAL</li>
            </ol>
            <p style={{ marginBottom: 8 }}><strong>Phase 2 — Proposal Draft</strong> (opt-in, ~$0.25):</p>
            <p style={{ marginBottom: 14, paddingLeft: 12 }}>Only run this AFTER reading the executive decision and deciding to pursue. Skips the "we wrote it, might as well submit" trap.</p>
            <p style={{ marginTop: 14, color: 'var(--text-2)', fontSize: 12 }}>
              Output is markdown — copy into Google Docs / Word for final formatting.
            </p>
          </div>
        )}

        {/* ── PHASE 1 SECTIONS ─────────────────────────────────────────── */}
        <Section
          title="Opportunity Identity Resolution"
          content={draft?.identity}
          busy={running && !draft?.identity && !draft?.research}
          error={draft?.identityError}
          badge={draft?.identityAt ? new Date(draft.identityAt).toLocaleTimeString() : null}
        />
        <Section
          title="Research Brief"
          content={draft?.research}
          busy={running && draft?.identity && !draft?.research}
          error={draft?.researchError}
          badge={draft?.researchAt ? new Date(draft.researchAt).toLocaleTimeString() : null}
        />
        <Section
          title="Business Value Scorecard"
          content={draft?.businessValue}
          busy={running && draft?.research && !draft?.businessValue && !draft?.draft}
          error={draft?.businessValueError}
          badge={draft?.businessValueAt ? new Date(draft.businessValueAt).toLocaleTimeString() : null}
        />
        <Section
          title="Red Team Review (Capture)"
          content={draft?.redTeam}
          busy={running && draft?.businessValue && !draft?.redTeam && !draft?.draft}
          error={draft?.redTeamError}
          badge={draft?.redTeamAt ? new Date(draft.redTeamAt).toLocaleTimeString() : null}
        />
        <Section
          title="Executive Decision — The $40K Test"
          content={draft?.executiveDecision}
          busy={running && draft?.redTeam && !draft?.executiveDecision && !draft?.draft}
          error={draft?.executiveDecisionError}
          badge={draft?.executiveDecisionAt ? new Date(draft.executiveDecisionAt).toLocaleTimeString() : null}
        />

        {/* ── PHASE 2 GATE — "Draft Proposal" button appears once capture is done ── */}
        {draft?.executiveDecision && !draft?.draft && !draft?.finalDraft && (
          <div style={{ margin: '20px 0', padding: '16px 18px', background: 'var(--bg-2)', border: '1px solid var(--accent)', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-0)', marginBottom: 6 }}>Phase 2 — Proposal Draft</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.55, marginBottom: 12 }}>
              Read the Executive Decision above. If the verdict is YES (or CONDITIONAL and the gate is met), generate the proposal draft. If NO, don't spend the credits.
            </div>
            <button
              onClick={startProposal}
              disabled={running}
              style={{
                fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: running ? 'not-allowed' : 'pointer',
                padding: '8px 16px', borderRadius: 4, background: 'var(--accent)', color: 'white', border: 'none',
                opacity: running ? 0.5 : 1,
              }}
            >
              {running ? 'Working…' : 'Generate Proposal Draft →'}
            </button>
          </div>
        )}

        {/* ── PHASE 2 SECTIONS (only visible if user clicked Draft Proposal) ── */}
        {(draft?.draft || draft?.finalDraft) && (
          <>
            <Section
              title="Draft Proposal"
              content={draft?.draft}
              busy={running && draft?.executiveDecision && !draft?.draft}
              error={draft?.draftError}
              badge={draft?.draftAt ? new Date(draft.draftAt).toLocaleTimeString() : null}
            />
            <Section
              title="Red Team Review (Proposal)"
              content={draft?.proposalRedTeam}
              busy={running && draft?.draft && !draft?.proposalRedTeam}
              error={draft?.proposalRedTeamError}
              badge={draft?.proposalRedTeamAt ? new Date(draft.proposalRedTeamAt).toLocaleTimeString() : null}
            />
            <Section
              title="Final Draft (Red-Team-Informed Revision)"
              content={draft?.finalDraft}
              busy={running && draft?.proposalRedTeam && !draft?.finalDraft}
              error={draft?.finalDraftError}
              badge={draft?.finalDraftAt ? new Date(draft.finalDraftAt).toLocaleTimeString() : null}
            />
          </>
        )}
      </div>
    </div>
  );
}
